import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);

    // Decode the small flying photo as soon as the finger / mouse is on the button, so the throw
    // starts instantly on click with nothing left to load.
    const warm = (event) => { if (event.target.closest?.('button[name="add"]')) this.#warmFlyer(); };
    this.addEventListener('pointerdown', warm, opts);
    this.addEventListener('pointerover', warm, opts);
  }

  #flyerCache = new Map();

  #flyerSource() {
    return (
      document.querySelector('[data-stage] [data-active] img') ||
      document.querySelector('[data-stage] img') ||
      this.querySelector('img')
    );
  }

  #flyerSrc(source) {
    const rawSrc = source.currentSrc || source.src;
    return rawSrc ? rawSrc.replace(/([?&])width=\d+/, '$1width=300') : '';
  }

  #warmFlyer() {
    const source = this.#flyerSource();
    if (!source) return;
    const src = this.#flyerSrc(source);
    if (!src || this.#flyerCache.has(src)) return;
    const img = new Image();
    img.src = src;
    this.#flyerCache.set(src, img.decode().catch(() => {}));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"], button[name="xo-buy-now"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const line = this.#lineFromFormData(new FormData(form));
    if (!line) {
      this.#abortController = null;
      return;
    }

    this.#setSubmitting(true, submitter);
    this.#showError('');

    // The throw starts right away (before the cart is updated and redrawn, which used to make it
    // stutter); the cart icon bounces once both the throw and the cart update are done.
    // «شراء الآن» (theme button): add the product, then go straight to checkout (no flying photo).
    const buyNow = submitter instanceof HTMLElement && submitter.getAttribute('name') === 'xo-buy-now';
    if (buyNow) {
      // «شراء الآن» / «احجز الآن»: straight to checkout with only this product, the cart is left
      // untouched (a checkout link with the item, its quantity and its notes such as «حجز مسبق»).
      const variantId = String(line.merchandiseId).split('/').pop();
      const params = new URLSearchParams();
      if (line.attributes?.length) {
        const props = Object.fromEntries(line.attributes.map((a) => [a.key, a.value]));
        const json = JSON.stringify(props);
        params.set('properties', btoa(String.fromCharCode(...new TextEncoder().encode(json))));
      }
      const locale = document.documentElement.lang;
      if (locale) params.set('locale', locale);
      const query = params.toString();
      window.addEventListener('pageshow', () => this.#setSubmitting(false, submitter), { once: true });
      // The checkout link makes its own cart; remember the visitor's cart so it is put back as it was
      // when they come back without paying (see xoRestoreCart in layout/theme.liquid).
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(1500) });
        const cart = await res.json();
        const items = (cart.items || []).map((i) => ({ id: i.variant_id, quantity: i.quantity, properties: i.properties || {} }));
        localStorage.setItem('xo-cart-restore', JSON.stringify({ t: Date.now(), items }));
      } catch {}
      window.location.assign(`/cart/${variantId}:${line.quantity}${query ? `?${query}` : ''}`);
      return;
    }
    const flight = this.#flyToCart(submitter);

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        flight.cancel();
        this.#showError(errorMessage);
      } else {
        // Merchant choice: do not open the cart drawer; fly the product image into the header cart instead.
        flight.succeed();
        if (buyNow) {
          const root = window.Shopify?.routes?.root || '/';
          window.location.assign(`${root.replace(/\/?$/, '/')}checkout`);
          return;
        }
      }
    } catch (error) {
      flight.cancel();
      if (signal.aborted) return;

      // Stock / quantity problems keep the store's own wording; anything else (slow or lost
      // connection, store busy) gets the formal connection note.
      this.#showError(error?.cause?.userErrors?.[0]?.message || this.dataset.errorMessage || getCartErrorMessage(error) || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  // The header is hidden while scrolling down: bring it back (its normal slide-in) and return the
  // cart icon with the spot it will have once the header is shown.
  #revealHeaderCart() {
    const header = document.querySelector('s-header');
    const icon = document.getElementById('header-cart-trigger');
    if (!header || !icon || header.dataset.hidden !== 'true') return null;
    header.style.setProperty('transition', 'none', 'important');
    header.dataset.hidden = 'false';
    const rect = icon.getBoundingClientRect();
    header.dataset.hidden = 'true';
    void header.offsetWidth;
    header.style.removeProperty('transition');
    requestAnimationFrame(() => { header.dataset.hidden = 'false'; });
    if (!rect.width || !rect.height) return null;
    return { el: icon, rect };
  }

  // The cart button the photo flies into: the header one, or (phone app / header hidden) any other
  // visible cart button, such as the app's bottom bar.
  #visibleCartButton() {
    const list = [document.getElementById('header-cart-trigger'), ...document.querySelectorAll('[commandfor="cart-drawer"]')];
    return list.find((el) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight;
    }) || null;
  }

  // Raise the number on every cart icon by `qty` now; returns a function that puts it back.
  #bumpCartCount(qty) {
    const undo = [];
    const header = document.querySelector('#header-cart-trigger > span');
    if (header) {
      let badge = header.querySelector('span.bg-brand');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'absolute -top-1.5 -end-2 flex h-4 min-w-4 items-center justify-center bg-brand px-1 text-[10px] font-bold text-ink';
        badge.textContent = '0';
        header.append(badge);
        undo.push(() => badge.remove());
      } else {
        const before = badge.textContent;
        undo.push(() => { if (badge.isConnected) badge.textContent = before; });
      }
      badge.textContent = String((parseInt(badge.textContent, 10) || 0) + qty);
      badge.hidden = false;
    }
    document.querySelectorAll('[data-xo-app-cart-count]').forEach((badge) => {
      const before = badge.textContent;
      const wasHidden = badge.hidden;
      badge.textContent = String((parseInt(before, 10) || 0) + qty);
      badge.hidden = false;
      undo.push(() => { badge.textContent = before; badge.hidden = wasHidden; });
    });
    return () => undo.forEach((fn) => fn());
  }

  #flyToCart(submitter) {
    let ok = false;
    let failed = false;
    let landed = false;
    let resolveLanded;
    const landedPromise = new Promise((resolve) => { resolveLanded = resolve; });
    const setLanded = () => { landed = true; resolveLanded(); };
    let flyerEl = null;
    let anim = null;
    let badgeUndo = null;
    const qty = Math.max(1, parseInt(this.querySelector('[name="quantity"]')?.value, 10) || 1);

    // The moment the photo touches the cart: the icon bounces and its number goes up right away
    // (no waiting for the store); the real number replaces it when the cart refreshes.
    const touch = () => {
      if (failed) return;
      const icon = this.#visibleCartButton();
      if (icon) {
        icon.classList.remove('xo-cart-bump');
        void icon.offsetWidth;
        icon.classList.add('xo-cart-bump');
      }
      if (!ok) badgeUndo = this.#bumpCartCount(qty);
    };
    const control = {
      landed: landedPromise,
      succeed: () => { ok = true; },
      cancel: () => {
        failed = true;
        if (anim) anim.cancel();
        else flyerEl?.remove();
        badgeUndo?.();
        setLanded();
      },
    };

    // Header hidden (scrolled down): it slides back in and the photo flies to where its cart icon lands.
    const revealed = this.#revealHeaderCart();
    const target = revealed?.el || this.#visibleCartButton();
    let source = this.#flyerSource();
    if (!target || !source || !source.getBoundingClientRect().width) { setLanded(); return control; }

    const phone = window.matchMedia('(hover: none), (pointer: coarse)').matches;
    let from = source.getBoundingClientRect();
    // Phones: the photo is usually scrolled out of sight by the time the button is pressed, so the
    // throw starts from the button itself.
    if ((from.bottom < 40 || from.top > innerHeight - 40) && submitter instanceof HTMLElement) {
      const b = submitter.getBoundingClientRect();
      const s = Math.min(110, b.height * 2.2);
      from = { left: b.left + b.width / 2 - s / 2, top: b.top + b.height / 2 - s / 2, width: s, height: s };
    }
    const to = revealed?.rect || target.getBoundingClientRect();
    const size = Math.min(from.width, from.height, phone ? 120 : 220); // phones: a smaller photo is cheaper to move
    const startX = from.left + from.width / 2 - size / 2;
    const startY = from.top + from.height / 2 - size / 2;
    const dx = to.left + to.width / 2 - (startX + size / 2);
    const dy = to.top + to.height / 2 - (startY + size / 2);

    const flyer = document.createElement('img');
    flyerEl = flyer;
    // A small copy of the photo (decoded before it moves) so the throw never stutters on phones.
    const flyerSrc = this.#flyerSrc(source);
    flyer.src = flyerSrc;
    flyer.decoding = 'sync';
    flyer.alt = '';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.className = 'xo-cart-flyer';
    Object.assign(flyer.style, { left: `${startX}px`, top: `${startY}px`, width: `${size}px`, height: `${size}px`, opacity: '0', transform: 'translate3d(0, 0, 0)' });
    document.body.append(flyer);

    const lift = Math.min(160, Math.abs(dy) * 0.5 + 60);
    const run = () => {
      if (landed) { flyer.remove(); return; } // cancelled before it took off
      flyer.style.opacity = '';
      // Thrown in an arc: up first, then down into the cart while shrinking (GPU transforms only).
      const animation = flyer.animate(
        [
          { transform: 'translate3d(0, 0, 0) scale(1) rotate(0deg)', opacity: 1, offset: 0 },
          { transform: `translate3d(${dx * 0.45}px, ${dy * 0.45 - lift}px, 0) scale(0.6) rotate(-12deg)`, opacity: 1, offset: 0.45 },
          { transform: `translate3d(${dx}px, ${dy}px, 0) scale(0.08) rotate(8deg)`, opacity: 0.6, offset: 1 },
        ],
        { duration: 750, easing: 'cubic-bezier(0.5, 0, 0.3, 1)', fill: 'forwards' }
      );
      anim = animation;
      animation.onfinish = () => { flyer.remove(); setLanded(); touch(); };
      animation.oncancel = () => flyer.remove();
    };
    // already decoded while the mouse / finger was on the button: take off on the next frame
    const warmed = this.#flyerCache.get(flyerSrc);
    const timeout = new Promise((resolve) => setTimeout(resolve, warmed ? 120 : 250));
    Promise.race([Promise.all([warmed, flyer.decode().catch(() => {})]), timeout]).then(() => requestAnimationFrame(run));
    return control;
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;
    clearTimeout(error._t); clearTimeout(error._t2);

    if (!message) {
      error.hidden = true;
      error.textContent = '';
      error.classList.remove('is-in', 'is-out');
      return;
    }
    // The note pops out under the button that was pressed (add to cart or buy now).
    const anchor = this.#activeSubmitter?.closest?.('.xo-qty-anchor');
    if (anchor && error.parentElement !== anchor) anchor.append(error);
    // Same small red note as the quantity limit: pops out under the button, then goes away by itself.
    error.textContent = message;
    error.hidden = false;
    error.classList.remove('is-in', 'is-out');
    void error.offsetWidth;
    error.classList.add('is-in');
    error._t = setTimeout(() => {
      error.classList.add('is-out');
      error._t2 = setTimeout(() => { error.hidden = true; error.classList.remove('is-in', 'is-out'); }, 250);
    }, 4000);
  }
  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
